// DEPOSITO, LATO CLIENT
//
// Prima qui c'era un pulsante "TEST DEPOSIT 1 SOL" che chiamava `/api/deposit`
// con `{ amount: 1 }`: il server accreditava un SOL dal nulla, perche' si fidava
// del numero. Ora il giro e' quello vero:
//
//   1. si chiede al server dove mandare i soldi (`/api/chain`);
//   2. si costruisce un trasferimento SOL verso la tesoreria e lo si fa firmare
//      a Phantom — questa e' una transazione vera, con commissione vera;
//   3. si manda al server SOLO la signature. L'importo lo rilegge lui dalla
//      catena: qualunque cosa scrivessimo qui, non verrebbe guardata.
//
// Perche' si riprova: il server accredita solo a transazione **finalizzata**, e
// la finalizzazione richiede una decina di secondi. Nel frattempo risponde 202
// ("non ancora, riprova"). Riprovare e' sicuro perche' l'accredito e' idempotente
// sulla signature: cento tentativi con la stessa signature producono un solo
// accredito.

import { PublicKey, SystemProgram, Transaction, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { authHeader } from './authClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Quanto insistere prima di arrendersi. La transazione resta valida in catena
// anche dopo: al prossimo tentativo verrebbe accreditata comunque.
const TENTATIVI_MAX = 30;
const ATTESA_MS = 3000;

export async function fetchChainConfig() {
  const res = await fetch(`${API_BASE}/api/chain`);
  if (!res.ok) throw new Error('Configurazione della catena non disponibile');
  return res.json();
}

// L'impronta di ogni rete Solana. E' il blocco zero: due reti diverse non
// possono averla uguale, e non dipende da quale fornitore RPC si usa.
const GENESIS = {
  'mainnet-beta': '5eykt4UsFv8P8NJdTREpY1vzqKqZKvdpKuc147dw2N9d',
  devnet: 'EtWTRABZaYq6iMfeYKouRu166VU2xqa1wcaWoxPkrZBG',
  testnet: '4uhcVJyU9pJkvQyS88uRDiswHXSCkY3zQawwpjk2NsNY',
};

// Il controllo piu' importante di questo file, e va fatto PRIMA di muovere
// qualsiasi cosa: siamo davvero sulla rete che il server sta guardando?
//
// Se il wallet fosse su mainnet e il server cercasse le transazioni su devnet,
// il deposito partirebbe con soldi veri e non verrebbe trovato mai. Nessun
// errore, nessun avviso: soldi usciti e mai accreditati. Meglio rifiutare il
// deposito che scoprirlo dopo.
async function verificaRete(connection, clusterAtteso) {
  const atteso = GENESIS[clusterAtteso];
  if (!atteso) throw new Error(`Rete "${clusterAtteso}" sconosciuta: deposito annullato.`);

  const effettivo = await connection.getGenesisHash();
  if (effettivo !== atteso) {
    const trovata = Object.keys(GENESIS).find((k) => GENESIS[k] === effettivo) || 'sconosciuta';
    throw new Error(
      `Rete sbagliata: il wallet è su "${trovata}", il server verifica su "${clusterAtteso}". ` +
      'Deposito annullato prima di muovere fondi.'
    );
  }
}

// Manda la signature al server e traduce la risposta in un esito leggibile.
async function richiediAccredito(wallet, signature) {
  const res = await fetch(`${API_BASE}/api/deposit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader(wallet) },
    body: JSON.stringify({ signature }),
  });

  const dati = await res.json().catch(() => ({}));

  if (res.ok) return { stato: 'accreditato', dati };
  // Gia' accreditato: e' successo, non un errore. Capita riprovando dopo che il
  // primo tentativo era andato a buon fine ma la risposta si era persa.
  if (res.status === 409) return { stato: 'gia-accreditato', dati };
  // 202 = non ancora finalizzata; 503 = RPC momentaneamente non raggiungibile.
  if (res.status === 202 || res.status === 503) return { stato: 'attendi', dati };

  const err = new Error(dati.error || 'Deposito rifiutato');
  err.code = dati.code;
  throw err;
}

/**
 * Esegue il deposito completo.
 *
 * @param {object} opts
 * @param {object} opts.connection    connessione Solana (useConnection)
 * @param {PublicKey} opts.publicKey  wallet connesso
 * @param {function} [opts.signTransaction] dal wallet adapter (via preferita)
 * @param {function} opts.sendTransaction  dal wallet adapter (ripiego)
 * @param {number} opts.sol           quanti SOL depositare
 * @param {function} [opts.onProgress] riceve messaggi di stato da mostrare
 */
export async function deposita({ connection, publicKey, signTransaction, sendTransaction, sol, onProgress = () => {} }) {
  const config = await fetchChainConfig();

  if (!config.depositsEnabled) {
    throw new Error('I depositi non sono attivi su questo server.');
  }
  if (sol < config.minDepositSol) {
    throw new Error(`Deposito minimo: ${config.minDepositSol} SOL.`);
  }

  // Prima di tutto il resto: la rete giusta. Vedi verificaRete.
  await verificaRete(connection, config.cluster);

  const lamports = Math.round(sol * LAMPORTS_PER_SOL);
  const wallet = publicKey.toString();

  onProgress('Confirm the transaction in your wallet…');

  const transazione = new Transaction().add(
    SystemProgram.transfer({
      fromPubkey: publicKey,
      toPubkey: new PublicKey(config.treasury),
      lamports,
    })
  );

  // Il blockhash serve alla transazione per avere una scadenza: senza, il wallet
  // non saprebbe entro quando e' valida.
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
  transazione.recentBlockhash = blockhash;
  transazione.feePayer = publicKey;

  // FIRMIAMO con il wallet ma TRASMETTIAMO noi.
  //
  // Non è un dettaglio di stile. `sendTransaction` dell'adapter Phantom chiama
  // `signAndSendTransaction`: è Phantom a trasmettere, e lo fa sulla rete
  // selezionata *dentro Phantom* — che è indipendente da quella dell'app e che
  // `verificaRete` (qui sopra) non può vedere, perché guarda la connessione
  // dell'app. Con Phantom su mainnet e l'app su devnet, il deposito sarebbe
  // partito con SOL veri su una rete dove il server non guarda: soldi usciti e
  // mai accreditati, cioè esattamente il caso che verificaRete esiste per
  // impedire.
  //
  // Firmando e trasmettendo per conto nostro, la transazione finisce sulla rete
  // che abbiamo appena verificato, qualunque cosa Phantom abbia selezionato.
  // Resta il suo avviso «Simulazione della transazione non riuscita», che è
  // cosmetico: Phantom simula sulla propria rete, dove quel blockhash non
  // esiste. La transazione è valida e viene accettata.
  let signature;
  if (typeof signTransaction === 'function') {
    const firmata = await signTransaction(transazione);
    signature = await connection.sendRawTransaction(firmata.serialize());
  } else {
    // Wallet che non espone signTransaction: si ripiega sulla via dell'adapter.
    signature = await sendTransaction(transazione, connection);
  }

  onProgress('Transaction sent. Waiting for finalization…');

  // Prima aspettiamo la conferma dal nostro RPC: e' piu' rapido che bussare al
  // server a vuoto. Se fallisce non e' grave, il ciclo qui sotto insiste comunque.
  try {
    await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, 'finalized');
  } catch (e) {
    // Timeout della conferma: la transazione puo' essere passata lo stesso.
  }

  for (let tentativo = 1; tentativo <= TENTATIVI_MAX; tentativo++) {
    const esito = await richiediAccredito(wallet, signature);

    if (esito.stato === 'accreditato' || esito.stato === 'gia-accreditato') {
      return { signature, ...esito.dati, giaAccreditato: esito.stato === 'gia-accreditato' };
    }

    onProgress(`Waiting for finalization… (${tentativo}/${TENTATIVI_MAX})`);
    await new Promise((r) => setTimeout(r, ATTESA_MS));
  }

  // Non e' un fallimento del deposito: i soldi sono in catena e la signature
  // resta valida. Va detto all'utente in questi termini, non come un errore.
  const err = new Error(
    'La transazione è stata inviata ma non risulta ancora finalizzata. ' +
    `Il deposito verrà accreditato: conserva questa signature — ${signature}`
  );
  err.code = 'ANCORA_IN_CORSO';
  err.signature = signature;
  throw err;
}

// Riprende un deposito interrotto (scheda chiusa, pagina ricaricata) a partire
// dalla sola signature: e' l'unico dato che serve.
export async function riprendiDeposito(wallet, signature) {
  return richiediAccredito(wallet, signature);
}
